import { useState, useEffect, useCallback } from 'react';
import { api, fmt } from '../api';
import toast from 'react-hot-toast';
import NavIcon from '../components/NavIcon';
import { confirmDialog } from '../components/ConfirmDialog';

// ─── Table order modal: add/edit items, then Pay or Cancel ─────────────────────
function TableModal({ tableNumber, products, onClose, onChanged }) {
  const [order,    setOrder]    = useState(null); // null while loading
  const [loading,  setLoading]  = useState(true);
  const [productId, setProductId] = useState('');
  const [quantity,  setQuantity]  = useState(1);
  const [adding,    setAdding]    = useState(false);
  const [paying,    setPaying]    = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.getTableOrder(tableNumber);
      setOrder(res.order);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [tableNumber]);

  useEffect(() => { refresh(); }, [refresh]);

  const items = order?.items ?? [];
  const total = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!productId || Number(quantity) <= 0) return toast.error('Select a product and quantity');
    setAdding(true);
    try {
      const updated = await api.addTableItem(tableNumber, { productId: Number(productId), quantity: Number(quantity) });
      setOrder(updated);
      setProductId('');
      setQuantity(1);
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleQtyChange = async (itemId, newQty) => {
    const qty = Number(newQty);
    if (isNaN(qty)) return;
    try {
      const updated = await api.updateTableItem(tableNumber, itemId, { quantity: qty });
      setOrder(updated);
      onChanged();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRemove = async (itemId) => {
    try {
      const updated = await api.removeTableItem(tableNumber, itemId);
      setOrder(updated);
      onChanged();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePay = async () => {
    if (items.length === 0) return toast.error('Add at least one item before marking paid');
    if (!(await confirmDialog(`Mark Table ${tableNumber} as paid for ${fmt.currency(total)}? This records the sale and frees the table.`, { confirmText: 'Mark Paid' }))) return;
    setPaying(true);
    try {
      await api.payTable(tableNumber);
      toast.success(`Table ${tableNumber} paid — sale recorded!`);
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPaying(false);
    }
  };

  const handleCancel = async () => {
    if (!(await confirmDialog(`Cancel the order for Table ${tableNumber}? No sale will be recorded.`, { danger: true, confirmText: 'Cancel Order' }))) return;
    setCancelling(true);
    try {
      await api.cancelTable(tableNumber);
      toast.success(`Table ${tableNumber} order cancelled`);
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Table {tableNumber}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div className="empty-state"><div className="spinner" /></div>
        ) : (
          <div className="modal-body">
            {/* Add item row */}
            <form onSubmit={handleAdd} className="table-order-add-form" style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                <label>Product</label>
                <select value={productId} onChange={e => setProductId(e.target.value)}>
                  <option value="">Select a product…</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} — {fmt.currency(p.salePrice)}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ width: 90, marginBottom: 0 }}>
                <label>Qty</label>
                <input type="number" min="0.01" step="0.01" value={quantity} onChange={e => setQuantity(e.target.value)} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={adding}>
                {adding ? 'Adding…' : '+ Add'}
              </button>
            </form>

            {/* Items list */}
            <div className="section-title mt-4">Order Items</div>
            {items.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--muted)', padding: '8px 2px' }}>
                No items yet — add something above.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.map(item => (
                  <div key={item.id} className="table-order-item-row">
                    <span>{item.product?.name ?? 'Unknown product'}</span>
                    <input
                      type="number" min="0" step="0.01" defaultValue={item.quantity}
                      onBlur={e => handleQtyChange(item.id, e.target.value)}
                      style={{ padding: '4px 6px', fontSize: 12 }}
                      className="input"
                    />
                    <span style={{ color: 'var(--muted)' }}>{fmt.currency(item.unitPrice)}</span>
                    <span style={{ fontWeight: 700 }}>{fmt.currency(item.quantity * item.unitPrice)}</span>
                    <button className="btn-sm btn-danger" onClick={() => handleRemove(item.id)} title="Remove">✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Total */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 0', borderTop: '1px solid var(--border)', marginTop: 4,
            }}>
              <span className="section-title" style={{ margin: 0 }}>Total</span>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{fmt.currency(total)}</span>
            </div>
          </div>
        )}

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button className="btn-ghost" onClick={handleCancel} disabled={cancelling || loading || items.length === 0}>
            {cancelling ? 'Cancelling…' : 'Cancel Order'}
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn-ghost" onClick={onClose}>Close</button>
            <button className="btn btn-success" onClick={handlePay} disabled={paying || loading || items.length === 0}>
              {paying ? 'Processing…' : `✓ Paid — ${fmt.currency(total)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Table grid card ────────────────────────────────────────────────────────────
function TableCard({ tableNumber, order, onClick }) {
  const occupied = !!order && order.items?.length > 0;
  const total = occupied ? order.items.reduce((s, i) => s + i.quantity * i.unitPrice, 0) : 0;

  return (
    <button
      onClick={onClick}
      className="card"
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 6, padding: '20px 10px', cursor: 'pointer', textAlign: 'center',
        border: occupied ? '1px solid var(--accent)' : '1px solid var(--border)',
        background: occupied ? 'rgba(204,122,61,0.06)' : 'var(--surface)',
        transition: 'all .15s',
        font: 'inherit',
        color: 'inherit',
      }}
    >
      <NavIcon name={occupied ? 'tableorders' : 'chair'} size={22} />
      <span style={{ fontWeight: 700, fontSize: 15 }}>Table {tableNumber}</span>
      {occupied ? (
        <>
          <span className="badge badge-blue">{order.items.length} item{order.items.length !== 1 ? 's' : ''}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{fmt.currency(total)}</span>
        </>
      ) : (
        <span className="badge badge-none">Free</span>
      )}
    </button>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────────
export default function TableOrders() {
  const [tables,    setTables]    = useState([]);
  const [products,  setProducts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null); // table number or null

  const load = useCallback(async () => {
    try {
      const res = await api.getTableOrders();
      setTables(res.tables ?? []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.getProductsLookup().then(setProducts).catch(() => setProducts([]));
  }, [load]);

  const occupiedCount = tables.filter(t => t.order?.items?.length > 0).length;

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h2>Table Orders</h2>
        <p>{occupiedCount} of {tables.length} tables occupied — tap a table to build or bill its order</p>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 14,
      }}>
        {tables.map(t => (
          <TableCard
            key={t.tableNumber}
            tableNumber={t.tableNumber}
            order={t.order}
            onClick={() => setSelected(t.tableNumber)}
          />
        ))}
      </div>

      {selected && (
        <TableModal
          tableNumber={selected}
          products={products}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
